import { useState, useEffect, useCallback } from 'react'
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  type AnnouncementSummary,
  type AnnouncementFilters,
  type AnnouncementCreateData,
  type AnnouncementLevel,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { AnnouncementFormModal } from './AnnouncementFormModal'

const activeOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
]

const levelOptions = [
  { value: '', label: 'All levels' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
]

const LEVEL_BADGE: Record<AnnouncementLevel, string> = {
  info: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      }`}
    >
      {isActive ? 'active' : 'inactive'}
    </span>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function Announcements() {
  const [items, setItems] = useState<AnnouncementSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<AnnouncementFilters>({
    is_active: '',
    search: '',
    page: 1,
    page_size: 50,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) =>
      prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }
    )
  }, [debouncedSearch])

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editing, setEditing] = useState<AnnouncementSummary | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const canCreate = usePermission('announcements', 'create')
  const canEdit = usePermission('announcements', 'edit')
  const canDelete = usePermission('announcements', 'delete')

  const loadAnnouncements = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAnnouncements(filters)
      if (response.ok && response.data) {
        setItems(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load announcements')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadAnnouncements()
  }, [loadAnnouncements])

  const handleFilterChange = (key: keyof AnnouncementFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleActiveToggle = async (id: string, isActive: boolean) => {
    const response = await updateAnnouncement(id, { is_active: isActive })
    if (response.ok) {
      loadAnnouncements()
    } else {
      setError(response.error?.message || 'Failed to update announcement')
    }
  }

  const handleDelete = async (id: string) => {
    const response = await deleteAnnouncement(id)
    if (response.ok) {
      setDeleteConfirmId(null)
      loadAnnouncements()
    } else {
      setError(response.error?.message || 'Failed to remove announcement')
    }
  }

  const handleCreate = async (data: AnnouncementCreateData) => {
    const response = await createAnnouncement(data)
    if (response.ok) {
      loadAnnouncements()
    } else {
      throw new Error(response.error?.message || 'Failed to publish announcement')
    }
  }

  const handleUpdate = async (data: AnnouncementCreateData) => {
    if (!editing) return
    const response = await updateAnnouncement(editing.id, data)
    if (response.ok) {
      loadAnnouncements()
    } else {
      throw new Error(response.error?.message || 'Failed to save announcement')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
            Announcements
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {total} site-wide banner{total === 1 ? '' : 's'}
          </p>
        </div>
        {canCreate && (
          <CustomButton variant="primary" onClick={() => setShowCreateModal(true)}>
            New Announcement
          </CustomButton>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search messages..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.is_active || ''}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {activeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={filters.level || ''}
          onChange={(e) => handleFilterChange('level', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {levelOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#503864] border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
          No announcements yet
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#3d2c4f]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#1a1520]">
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                  Message
                </th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                  Level
                </th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                  Window
                </th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                  Status
                </th>
                {(canEdit || canDelete) && (
                  <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-100 dark:border-[#3d2c4f] hover:bg-gray-50 dark:hover:bg-[#2d2236]"
                >
                  {/* Message */}
                  <td className="max-w-md px-3 py-3">
                    <div
                      className="truncate font-medium text-gray-900 dark:text-white"
                      title={item.message}
                    >
                      {item.message}
                    </div>
                    {item.link_url && (
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {item.link_label || 'Learn more'} → {item.link_url}
                      </div>
                    )}
                  </td>
                  {/* Level */}
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL_BADGE[item.level]}`}
                    >
                      {item.level}
                    </span>
                    {!item.dismissible && (
                      <span
                        className="ml-1 text-xs text-gray-400"
                        title="Users cannot dismiss this banner"
                      >
                        · locked
                      </span>
                    )}
                  </td>
                  {/* Window */}
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {item.starts_at || item.ends_at ? (
                      <>
                        {formatDate(item.starts_at)} → {formatDate(item.ends_at)}
                      </>
                    ) : (
                      'Always'
                    )}
                  </td>
                  {/* Status */}
                  <td className="px-3 py-3">
                    {canEdit ? (
                      <select
                        value={item.is_active ? 'true' : 'false'}
                        onChange={(e) => handleActiveToggle(item.id, e.target.value === 'true')}
                        className="cursor-pointer rounded-full border-0 bg-transparent text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      >
                        <option value="true">active</option>
                        <option value="false">inactive</option>
                      </select>
                    ) : (
                      <ActiveBadge isActive={item.is_active} />
                    )}
                  </td>
                  {/* Actions */}
                  {(canEdit || canDelete) && (
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3">
                        {canEdit && (
                          <button
                            onClick={() => setEditing(item)}
                            className="text-xs font-medium text-[#7f4f9a] hover:text-[#503864] dark:text-[#b58fcc]"
                          >
                            Edit
                          </button>
                        )}
                        {canDelete &&
                          (deleteConfirmId === item.id ? (
                            <span className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="text-xs font-medium text-red-600 hover:text-red-800"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(item.id)}
                              className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              title="Remove"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                />
                              </svg>
                            </button>
                          ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <AnnouncementFormModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreate} />
      )}

      {/* Edit Modal */}
      {editing && (
        <AnnouncementFormModal
          announcement={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}
    </div>
  )
}
